import { Injectable } from '@angular/core'
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Observable, of, throwError } from 'rxjs'
import { AuthService } from './auth.service'
import { catchError } from 'rxjs/operators'
import { MessageService } from 'primeng/api'
import { NgxSpinnerService } from 'ngx-spinner'
import { TranslateService } from '@ngx-translate/core'
@Injectable({
  providedIn: 'root',
})
export class AuthInterceptorService implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.authService.getToken()
    if (token)
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        this.spinner.hide()
        // handleAuthError deliberately emits null / err.message as the response value
        // when it swallows an auth failure — callers have always received those.
        return this.handleAuthError(err, req.url) as Observable<HttpEvent<unknown>>
      }),
    )
  }

  private handleAuthError(err: HttpErrorResponse, url: string): Observable<unknown> {
    const isMeEndpoint = url.indexOf('/auth/me') > 0
    // login, forgot-password, validate-pin, validate-pin-2fa, reset-password — anything under /auth/
    const isAuthFlowEndpoint = url.indexOf('/auth/') > 0
    const hasSession = !!this.authService.getToken()

    // The /auth/me probe: a failure with no token is an expected miss — swallow it.
    if (isMeEndpoint) {
      return hasSession ? throwError(() => err) : of(null)
    }

    // A 401/403 on the auth flow itself (bad credentials / wrong code) or any
    // request made while signed out is expected — there is no live session to
    // expire. Let the page that made the request handle it instead of forcing a
    // global logout + redirect (which used to bounce users off the reset page).
    if (isAuthFlowEndpoint || !hasSession) {
      return throwError(() => err)
    }

    // An authenticated user hit a protected endpoint and the session is no longer valid.
    const errorBody = err.error as { message?: string; code?: string } | null | undefined
    if (errorBody?.message != 'TWO_FACTOR_AUTH') {
      switch (err.status) {
        case 401:
          // Check if this is a session expired due to time restrictions
          if (errorBody?.code === 'SESSION_EXPIRED_TIME_RESTRICTION') {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('ACCESS_HOURS.ERRORS.SESSION_EXPIRED_TITLE'),
              detail: this.translate.instant('ACCESS_HOURS.ERRORS.SESSION_EXPIRED'),
              life: 10000, // Show for 10 seconds
            })
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'error',
              detail: 'Wrong username or password',
            })
          }
          this.authService.logout()
          return of(err.message)
        case 403:
          // Check if this is a login denied due to time restriction
          if (errorBody?.code === 'LOGIN_DENIED_TIME_RESTRICTION') {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('ACCESS_HOURS.ERRORS.ACCESS_RESTRICTED'),
              detail: this.translate.instant('ACCESS_HOURS.ERRORS.LOGIN_DENIED'),
              life: 10000, // Show for 10 seconds
            })
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'error',
              detail: 'Session expired, please login again',
            })
          }
          this.authService.logout()
          return of(err.message)
      }
    }

    return throwError(() => err)
  }
}
